import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { BackCounterConfirmCompleteModalComponent } from "app/_components/back-counter-confirm-complete-modal/back-counter-confirm-complete-modal.component";
import { BackCounterGeneralRequestModalComponent } from "app/_components/back-counter-general-request-modal/back-counter-general-request-modal.component";
import { PICK } from "app/_components/back-counter-request-tab/back-counter-request-tab.utils";
import { HotFlagComponent } from "app/_components/hot-flag/hot-flag.component";
import { KnownPartBuyoutComponent } from "app/_components/known-part-buyout/known-part-buyout.component";
import { NotesComponent } from "app/_components/notes/notes.component";
import { PartSearchModalComponent } from "app/_components/part-search-modal/part-search-modal.component";
import { StockTransferOrderComponent } from "app/_components/stock-transfer-order/stock-transfer-order.component";
import { Branch } from "entities/branch";
import { Customer } from "entities/customer";
import { FileAttachment } from "entities/feedback-input";
import { GetInventoryRequest } from "entities/national-inventory/get-inventory-request";
import { InventoryResult } from "entities/national-inventory/inventory-result";
import { PartInventory } from "entities/national-inventory/part-inventory";
import { BackCounterNote } from "entities/notes";
import { OrderPhoto } from "entities/order-photo";
import { MscPriceVerifyData } from "entities/price-verify/msc-price-verify-data";
import { MscPriceVerifyPart } from "entities/price-verify/msc-price-verify-part";
import { ToastType } from "entities/toast-type";
import { mapPartToRequestOrderItem } from "helpers/back-counter-request-order-item-mapper";
import { AddAttachmentsComponent } from "modals/add-attachments/add-attachments.component";
import { BctPriceVerifyModalComponent } from "modals/bct-price-verify-modal/bct-price-verify-modal.component";
import { CancelBctRequestModalComponent } from "modals/cancel-bct-request-modal/cancel-bct-request-modal.component";
import { BinLocationDictionary } from "modals/override-bin-location-modal/override-bin-location-modal.component";
import { PriceOverrideModalComponent } from "modals/price-override-modal/price-override-modal.component";
import { EMPTY, Observable, Subject, of } from "rxjs";
import { catchError, filter, map, switchMap, takeUntil, tap, withLatestFrom } from "rxjs/operators";
import { BackCounterDashboardService } from "services/back-counter-dashboard.service";
import { BackCounterNotesService } from "services/back-counter-notes.service";
import { CustomerService } from "services/customer.service";
import { DownloadService } from "services/download.service";
import { LoaderService } from "services/loader.service";
import { LoggerService } from "services/logger.service";
import { ModalService } from "services/modal.service";
import { NationalInventoryService } from "services/national-inventory.service";
import { PartService } from "services/part.service";
import { ToastService } from "services/toast.service";
import { VendorService } from "services/vendor.service";
import { AppState } from "store/app-state";
import * as BackCounterToolActions from 'store/back-counter/back-counter.actions';
import * as BackCounterToolSelectors from 'store/back-counter/back-counter.selectors';
import * as BranchSelectors from "store/branch/branch.selectors";
import * as FeatureFlagSelectors from "store/feature-flags/feature-flags.selectors";
import * as actions from "./back-counter.actions";
import { BackCounterConfig, ToastMessages } from "./back-counter.config";
import { BackCounterRequestDetails, BackCounterRequestPartItemAction, RequestConnectionEvent, RequestNote, RequestOrderItem, RequestPartItemsUnsavedChanges, SapEventMessage } from "./back-counter.entities";
import { transformRequestOrderItemForBuyout, transformRequestOrderItemForHotFlag, transformRequestOrderItemForSTO } from "./back-counter.reducer.util";
import { selectBackCounterOpenRequests } from "./back-counter.selectors";

@Injectable()
export class BackCounterEffects {

openSearchModal$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(actions.addPartToRequest),
        switchMap(
          ({
            orderNumber,
            jobNumber,
            mainPrItem,
            rewardNumber,
            onSelectPart,
            branchCode,
          }) =>
            this.modalService.open(
              PartSearchModalComponent,
              {
                branch: { code: branchCode } as Branch,
                requestId: orderNumber,
                customer: { rewardNumber } as Customer,
                initialSearchTerm: "",
                displayType: "back-counter-tool",
                onSelectPart: (part: any) => {
                  const onSelectPartParams = mapPartToRequestOrderItem(
                    part,
                    { orderNumber, jobNumber, mainPrItem },
                    this.loggerService
                  );
                  this.loggerService.info(
                    "openSearchModal - onSelectPartParams: ",
                    onSelectPartParams
                  );
                  onSelectPart(onSelectPartParams);
                  this.toastService.showToast(
                    ToastMessages.ADD_PART_SUCCESS,
                    ToastType.Success
                  );
                },
              },
              { size: "lg" }
            )
        )
      ),
    { dispatch: false }
  );

  getRequests$ = createEffect(() => this.action$.pipe(
    ofType(actions.getBackCounterRequests),
    switchMap(({ payload }) => this.backCounterDashboardService.getRequests(payload)
      .pipe(
        map((response) =>
          response.ErrorType ?
            actions.getBackCounterRequestsFailed({ error: response }) :
            actions.getBackCounterRequestsSuccess({ requests: response })
        ),
        catchError(async (error) => actions.getBackCounterRequestsFailed({ error }))
      )
    )
  ));

  showErrorMessage$ = createEffect(() => this.action$.pipe(
    ofType(actions.getBackCounterRequestsFailed),
    tap((error) => {
      if( error && error?.error?.detail) {
        this.toastService.showToast(ToastMessages.ERROR_DETAILED_LOADINGREQUESTS(error.error.detail), ToastType.Error);
      }else{
        this.toastService.showToast(ToastMessages.ERROR_LOADINGREQUESTS, ToastType.Error);
      }
    })
  ), { dispatch: false });

  getRequestDetails$ = createEffect(() =>
    this.action$.pipe(
      ofType(actions.openBackCounterRequest),
      withLatestFrom(this.store.select(selectBackCounterOpenRequests)),
      filter(([{ requestDetails }, openRequests]) =>
        this.checkMaximumActiveRequests(
          requestDetails.orderNumber,
          openRequests
        )
      ),
      switchMap(([{ requestDetails }, openRequests]) => {
        const alreadyOpen = openRequests?.find(
          (i) => i.orderNumber === requestDetails.orderNumber && i.branch
        );
        if (alreadyOpen) {
          return of(
            actions.getRequestDetailsSuccess({
              requestDetails: alreadyOpen,
              US129696Enabled: true,
            })
          );
        }
        return this.backCounterDashboardService
          .getRequestDetails(requestDetails.orderNumber,
            requestDetails.roNumber,
            requestDetails.requestType,
            false
          )
          .pipe(
            map((requestDetails) =>
              actions.getRequestDetailsSuccess({
                requestDetails,
                US129696Enabled: true,
              })
            ),
            catchError((error) => {
              if (error.message === "LOCKED") {
                this.toastService.showToast(
                  ToastMessages.REQUEST_LOCKED(""),
                  ToastType.Error
                );
              }
              return of(actions.getRequestDetailsFailed({ error }));
            })
          );
      })
    )
  );

  getRequestDetailsToEdit$ = createEffect(() =>
    this.action$.pipe(
      ofType(actions.editBackCounterRequest),
      switchMap(({ requestDetails, inEditMode }) =>
        this.backCounterDashboardService
          .getRequestDetails(requestDetails.orderNumber,
            requestDetails.roNumber,
            requestDetails.requestType,
            inEditMode
          )
          .pipe(
            map((requestDetails) => {
              let isLocked = requestDetails.isLocked;
              if (!inEditMode) {
                this.store.dispatch(
                  actions.removeRequestLock({
                    orderNumber: requestDetails.orderNumber,
                  })
                );
              } else {
                isLocked = true;
              }

              return actions.getRequestDetailsSuccess({
                requestDetails: { ...requestDetails, isLocked },
                // TODO: Remove this boolean
                US129696Enabled: true,
              });
            }),
            catchError((error) => {
              if (error.title === "LOCKED") {
                this.toastService.showToast(
                  ToastMessages.REQUEST_LOCKED(error.detail),
                  ToastType.Error
                );
              }
              return of(actions.getRequestDetailsFailed({ error }));
            })
          )
      )
    )
  );

  unlockRequestDetails$ = createEffect(() => this.action$.pipe(
    ofType(
      actions.closeBackCounterRequest,
      actions.removeRequestLock
    ),
    withLatestFrom(this.store.select(selectBackCounterOpenRequests)),
    switchMap(([action, openRequests]) => {
      if(action.type === '[Back Counter] Close Request'){
        const isLocked = openRequests.find(f => f.orderNumber === action.requestId)?.isLocked;
        if(isLocked) {
          return this.backCounterDashboardService.deleteRequestDetailsLock(action.requestId);
        }
      } else {
        const isLocked = openRequests.find(f => f.orderNumber === action.orderNumber)?.isLocked;
        if(isLocked) {
          return this.backCounterDashboardService.deleteRequestDetailsLock(action.orderNumber);
        }
      }
    })
  ), {dispatch: false});

  getRequestAttachments$ = createEffect(() => this.action$.pipe(
    ofType(actions.getRequestAttachments),
    switchMap(({ orderNumber }) => this.backCounterDashboardService.getRequestAttachments(orderNumber).pipe(
      map((attachments) => actions.getRequestAttachmentsSuccess({ attachments, orderNumber })),
      catchError((error) => of(actions.getRequestAttachmentsFailed({ error })))
    ))
  ));

  openAttchament$ = createEffect(() => this.action$.pipe(
    ofType(actions.openAttachment),
    switchMap(({orderNumber, photoId, mimetype, fileName}) => this.backCounterDashboardService.getRequestAttachmentBase64(photoId, orderNumber).pipe(
      map((file) => {
        this.downloadService.downloadFileBase64(file.finalFileName.split('Item:')[1], mimetype, file.fileData);
        return of(actions.openAttachmentSuccess());
      }),
      catchError((error) => of(actions.openAttachmentFailed({error})))
    )),
  ), {dispatch: false});

  openGeneralRequestModal$ = createEffect(() => this.action$.pipe(
    ofType(actions.openGeneralRequestModal),
    switchMap(() => this.modalService.open(BackCounterGeneralRequestModalComponent).pipe(
      withLatestFrom(this.store.select(BranchSelectors.selectedBranch)),
      filter(([{note}, _]) =>
        note !== null && note !== ""),
          switchMap(( [{orderNumber, note, department}, branch]) => this.backCounterDashboardService.addGeneralRequest({orderNumber, noteText: note, branchCode: branch.code, department}).pipe(
          map((response) => {
            this.toastService.showToast(ToastMessages.GENERAL_REQUEST_ADD_SUCCESS, ToastType.Success);
            return actions.addGeneralRequestSuccess();
          }),
          catchError((error) => of(actions.addGeneralRequestFailed({error}))),
        )),
      ))
  ));

  updateGeneralRequest$ = createEffect(() => this.action$.pipe(
    ofType(actions.updateGeneralRequest),
    switchMap(({orderNumber, refOrderNumber, requestor, isComplete, department, assignedTo}) =>
      this.backCounterDashboardService.getGeneralRequestNotes(orderNumber).pipe(
        this.convertNoteTypes(),
        switchMap((notes) => {
            const updateNoteSubj = new Subject<{orderNumber: string; note: string, department: string}>();
            const addNoteSubj = new Subject<{orderNumber: string; note: string, department: string}>();

            const modal = this.ngbModalService.open(BackCounterGeneralRequestModalComponent);
            modal.componentInstance.updateGeneralRequest = true;
            modal.componentInstance.orderNumber = orderNumber;
            modal.componentInstance.refOrderNumber = refOrderNumber;
            modal.componentInstance.requestor = requestor;
            modal.componentInstance.notes = notes;
            modal.componentInstance.updateNote = updateNoteSubj;
            modal.componentInstance.addNote = addNoteSubj;
            modal.componentInstance.isComplete = isComplete;
            modal.componentInstance.department = department;
            modal.componentInstance.assignedTo = assignedTo;

            this.backCounterDashboardService.refreshEventSubject.pipe(
              filter(x => x.requestId === orderNumber.padStart(10, '0') && x.type === RequestConnectionEvent.newNoteOnGeneralRequest),
              takeUntil(modal.result),
              switchMap(() => this.getGeneralRequestNotes(orderNumber).pipe(
                tap((refreshedNotes: RequestNote[]) => {
                  modal.componentInstance.notes = refreshedNotes;
                })
              ))
            ).subscribe();

            return updateNoteSubj.pipe(
              switchMap(({ note, department }) =>
                this.backCounterDashboardService
                  .addNoteToGeneralRequest(orderNumber, note, department)
                  .pipe(
                    map(() => {
                      this.toastService.showToast(
                        `Successfully updated order #${orderNumber}`,
                        ToastType.Success
                      );
                      addNoteSubj.next({orderNumber, note, department});

                      return actions.updateGeneralRequestSuccess({
                        success: true,
                      });
                    }),
                    catchError((error) => {
                      if( error?.detail ){
                        this.toastService.showToast(
                          `Error updating order #${orderNumber}: ${error.detail}`,
                          ToastType.Error
                        );
                      }else{
                        this.toastService.showToast(
                          `Error updating order #${orderNumber}`,
                          ToastType.Error
                        );
                      }
                      return of(actions.updateGeneralRequestFailed({ error }));
                    }
                    )

                  )
              )
            );
          }
        ),
        catchError((error) => of(actions.getGeneralRequestNotesFailed({error})))
  ))));

  updateRequest$ = createEffect(() => this.action$.pipe(
    ofType(actions.updateBackCounterRequest),
    withLatestFrom(
      this.store.select(FeatureFlagSelectors.isFeatureActive('ChangeControl.NotForRelease'))
    ),
    switchMap(([{orderNumber, onComplete, payload}, notForRelease]) =>
      of(actions.ignoredUpdateBackCounterRequest()))
  ));

  updateBackCounterRequest$ = createEffect(() => this.action$.pipe(
    ofType(actions.updateBackCounterRequest),
    withLatestFrom(
      this.store.select(BackCounterToolSelectors.selectRequestPriceVerifications),
      this.store.select(BackCounterToolSelectors.selectBackCounterOpenRequests)
    ),
    switchMap(([action, allPriceVerifyParts, openedRequests]) => {
      let payloadCopy = JSON.parse(JSON.stringify(action.payload)) as RequestPartItemsUnsavedChanges; // Get a  deep copy of the payload
      const writableItemsToUpdate: RequestOrderItem[] =  payloadCopy.itemsToUpdate;
      const writableItemsToAdd: RequestOrderItem[] = payloadCopy.itemsToAdd;
      const priceVerifyParts = allPriceVerifyParts[action.orderNumber];

      // Helper function to apply price verification data to a part
      const applyPriceVerification = (part: RequestOrderItem) => {
        if(priceVerifyParts){
          const verifyPart = priceVerifyParts[part.materialNumber];
          if(verifyPart){
            part.priceVerifyComp = "X";
            part.priceVerifyRel = "X";
            part.manualPrice = verifyPart.unitPrice;
            part.manPrice = verifyPart.unitPrice.toString();
          }
        }
      };

      // Process items to update
      writableItemsToUpdate.forEach((part) => {
        if (part.freightOverride && parseFloat(part.freightOverride) > -1) {
          part.freightInbound = parseFloat(part.freightOverride);
          part.manualFreightFlag = "X";
        }
        applyPriceVerification(part);
      });

      // Process items to add - apply price verification if available
      writableItemsToAdd.forEach((part) => {
        applyPriceVerification(part);
      });
      const request = openedRequests?.find(i => i.orderNumber === action.orderNumber);
      payloadCopy.cumminsRapidServeControl = request?.crc;
      payloadCopy.engineSerialNumber = request?.esn;
      payloadCopy.vin = request?.vin;
      const isPickRequest = request.requestType === PICK;
      const requestId = isPickRequest ? request.roNumber : request.orderNumber;
      return this.backCounterDashboardService.updateRequestParts(requestId, payloadCopy).pipe(
        map((part) => {
          action.onComplete(true);
          this.toastService.showToast(ToastMessages.UPDATE_SUCCESS, ToastType.Success);
          return actions.updateBackCounterRequestSuccess({part, requestId: action.orderNumber});
        }),
        catchError((error) => {
          action.onComplete(false);
          if (error?.detail) {
            this.toastService.showToast(ToastMessages.UPDATE_DETAILED_FAILED(error.detail), ToastType.Error);
          } else {
            this.toastService.showToast(ToastMessages.UPDATE_FAILED, ToastType.Error);
          }
          return of(actions.updateBackCounterRequestFailed({error}));
        })
      );
    }
  )));

  handleErrors$ = createEffect(() => this.action$.pipe(
    ofType(actions.addGeneralRequestFailed),
    tap(({error}) => {
      this.toastService.showToast(error.detail, ToastType.Error);
    })
  ), {dispatch: false});

  backCounterMessageReceived$ = createEffect(() => this.action$.pipe(
    ofType(actions.backCounterMessageReceived),
    tap(({eventInfo} ) => {
      this.backCounterDashboardService.refreshEventSubject.next(eventInfo);
    })
  ), {dispatch: false});

  openRequestNotesModal$ = createEffect(() => this.action$.pipe(
    ofType(actions.openNotesModal),
    switchMap(({ config }) => this.modalService.open(NotesComponent, { config }))
  ), { dispatch: false });

  closeRequestNotesModal$ = createEffect(() => this.action$.pipe(
    ofType(actions.closeNotesModal),
    tap(({config} ) => {
      const simEventInfo: SapEventMessage = {
        type: config.type,
        requestId: config.id,
        noteId: ""
      };
      this.backCounterDashboardService.refreshEventSubject.next(simEventInfo);
    }),
  ), { dispatch: false });

  openConfirmCompleteModal$ = createEffect(() => this.action$.pipe(
    ofType(actions.openConfirmCompleteModal),
    switchMap(({ orderNumber, onCompleted }) => this.modalService.open(
      BackCounterConfirmCompleteModalComponent, { orderNumber },{ size: "sm", backdrop: "static", keyboard: false }
    ).pipe(
      switchMap(() => {
        this.store.dispatch(BackCounterToolActions.completeConfirmed({lockConfirm: true, orderNumber}));
        return this.backCounterDashboardService.completeRequest(orderNumber).pipe(
          tap((results) => {
            if(results?.ErrorType) {
              this.toastService.showToast(ToastMessages.DEFAULT_ERROR, ToastType.Error);
            } else {
              this.toastService.showToast(ToastMessages.REQUEST_COMPLETED, ToastType.Success);
              onCompleted();
            }
            this.store.dispatch(BackCounterToolActions.completeConfirmed({lockConfirm: false}));
          }),
          catchError((error) => {
            if (error?.detail) {
              this.toastService.showToast(ToastMessages.ERROR_DETAILED_COMPLETING_REQUEST(error.detail), ToastType.Error);
            } else {
              this.toastService.showToast(ToastMessages.DEFAULT_ERROR, ToastType.Error);
            }
            this.store.dispatch(BackCounterToolActions.completeConfirmed({lockConfirm: false}));
            return of(error);
          })
        );
      })
    ))
  ), { dispatch: false });

  completeGeneralRequest$ = createEffect(() => this.action$.pipe(
    ofType(actions.completeGeneralRequest),
    switchMap(({orderNumber, department, assignedTo}) => this.backCounterDashboardService.completeGeneralRequest(orderNumber, department, assignedTo).pipe(
      map((success) => {
        this.toastService.showToast(ToastMessages.GENERAL_REQUEST_COMPLETED, ToastType.Success);
        this.store.dispatch(BackCounterToolActions.backCounterMessageReceived({eventInfo:{
          noteId:'',
          type: RequestConnectionEvent.updatedRequest,
          requestId: orderNumber.padStart(10,'0')}}));
        return actions.completeGeneralRequestSuccess({success});
      }),
      catchError((error) => {
        this.toastService.showToast(ToastMessages.DEFAULT_ERROR, ToastType.Error);
        this.store.dispatch(
          actions.updateGeneralRequest({
            orderNumber,
            refOrderNumber: "",
            requestor: "",
            updateGeneralRequest: true,
            isComplete: false,
            department: "",
            assignedTo: "",
          }),
        );
        return of(actions.completeGeneralRequestFailed({error}));
      })
    ))
  ), {dispatch: false});

  private getPayerCardNumber(ppfsOrFcCardNumber: string, hinoCardNumber: string, request: BackCounterRequestDetails): string | null {
    const ppfsCard = ppfsOrFcCardNumber?.trim() || null;
    const hinoCard = hinoCardNumber?.trim() || null;
    const requestCard = request?.msccardNum?.trim() || null;

    if (ppfsCard === requestCard || hinoCard === requestCard) {
      return requestCard;
    }

    return "";
  }

  bctPriceVerifySuccess$ = createEffect(() => this.action$.pipe(
    ofType(BackCounterToolActions.BCTPriceVerifySuccess),
    tap(({toastMessages, verifiedParts, requestDetails, orderNumber}) => {
      const updatedParts = requestDetails.ordertogetitem.filter(x => x.materialNumber !== "" && x.materialNumber !== null).map(item => {
        const verifiedPart = verifiedParts.find(v => v.partNumber === item.materialNumber);
        if(verifiedPart){
          return {...item, priceVerifyComp: "X", priceVerifyRel: "X", manualPrice: verifiedPart.unitPrice, unsavedChanges: item.unsavedChanges ?? BackCounterRequestPartItemAction.update};
        } else {
          return {...item};
        }
      });
      if(toastMessages.length > 0) {
        this.toastService.showMultilineToast(toastMessages);
      } else {
        this.toastService.showToast("The Price has been verified and adjusted", ToastType.Success);
      }
      if (updatedParts?.filter(x => x.priceVerifyComp === 'X').length > 0) {
        return of(BackCounterToolActions.syncUnsavedChanges({
          orderNumber,
          editedParts: updatedParts
        }));
      }
    })
  ), {dispatch: false});

  openBCTPriceVerifyModal$ = createEffect(() => this.action$.pipe(
    ofType(BackCounterToolActions.openBCTPriceVerifyModal),
    switchMap(action =>
      this.customerService.getVerificationPayers({IncludeShipTo: false, SalesOrganization: "1000", AccountGroups: ["Z001", "Z002"], BranchCode: action.branchCode, rewardNumber: action.rewardNumber}).pipe(
        map(({ppfsOrFcCardNumber, hinoCardNumber, payers}) =>  ({...action, ppfsOrFcCardNumber, hinoCardNumber, payers})),
        withLatestFrom(
          this.store.select(BackCounterToolSelectors.selectRequestDetails(action.requestDetails.orderNumber))
        ),
      )
    ),
    filter(([{ppfsOrFcCardNumber, hinoCardNumber, payers, orderNumber}, request]) => {
      // Check if any card number is available using our helper method
      const payerCardNumber = this.getPayerCardNumber(ppfsOrFcCardNumber, hinoCardNumber, request);
      return !!payerCardNumber;
    }),
    switchMap(([{branchCode, rewardNumber, requestDetails, ppfsOrFcCardNumber, hinoCardNumber, payers, orderNumber}, request]) => {
      // Use helper method to determine the correct card number
      const payerCardNumber = this.getPayerCardNumber(ppfsOrFcCardNumber, hinoCardNumber, request);

      return this.modalService.open(BctPriceVerifyModalComponent, {payerCardNumber}).pipe(
        filter((selectedCardNumber) => selectedCardNumber !== null),
        withLatestFrom(
          this.store.select(BackCounterToolSelectors.selectRequestDetails(requestDetails.orderNumber))
        ),
        switchMap(([selectedCardNumber, request]) =>{
          const mscPriceVerifyParts = request.ordertogetitem
            .filter(x => x.materialNumber !== "" && x.materialNumber !== null).map(x => {
              return {
                partNumber: x.materialNumber,
                quantity: 1,
                unitPrice: x.unitPrice
              } as MscPriceVerifyPart
            });

          return this.partService.BCTMscPriceVerification({
            branchCode,
            rewardNumber,
            mscCardNumber: request.msccardNum,
            mscPriceVerifyParts,
            payerAccountNumber: request.payer,
            salesOrganization: null
          } as MscPriceVerifyData).pipe(
            map((priceVerifyParts) => {
              let toastMessages: { message: string; type: ToastType }[] = [];
              if (priceVerifyParts.messages.length) {
                toastMessages = priceVerifyParts.messages.map((x) => {
                  return { message: x.message, type: this.toastService.toToastType(x.level) }
                });
              }
              const verifiedParts = priceVerifyParts.priceVerifyItems.filter(x => x.isPriceVerified);
              return { priceVerifyParts, toastMessages, verifiedParts, orderNumber };
            }),
            map((data) => BackCounterToolActions.BCTPriceVerifySuccess({
              priceVerifyParts: data.priceVerifyParts,
              requestId: request.orderNumber,
              toastMessages: data.toastMessages,
              verifiedParts: data.verifiedParts,
              requestDetails,
              orderNumber: data.orderNumber
            })),
            catchError((error) => of(BackCounterToolActions.BCTPriceVerifyFailed({ error })))
          )
        })
      )
    })
  ));

  openBCTAttachmentsModal$ = createEffect(() => this.action$.pipe(
    ofType(actions.openBCTAttachmentsModal),
    withLatestFrom(this.store.select(selectBackCounterOpenRequests), this.store.select(BackCounterToolSelectors.selectOpenRequestsAttachments)),
    switchMap(([{orderNumber, itemNumber}, openRequests, openRequestsAttachments]) => {
      const modalRef = this.ngbModalService.open(AddAttachmentsComponent);
      modalRef.componentInstance.mode = "multiple";
      modalRef.componentInstance.alreadyAddedQty = openRequestsAttachments.find(x => x.orderNumber === orderNumber)?.attachments?.length ?? 0;
      return of({orderNumber, itemNumber, result: modalRef.result, openRequests});
    }),
    switchMap(({orderNumber, itemNumber, result, openRequests}) => {
      return result.then((fileAttachment: FileAttachment[]) => {
        const orderPhotos: OrderPhoto[] = fileAttachment.map(m => {
          return {
            ordernumber: orderNumber,
            itemnumber: itemNumber,
            filename: m.name,
            filevalue: m.base64File
          };
        });
        this.store.dispatch(BackCounterToolActions.attachmentSent({orderNumber, orderPhotos}));
      });
    })
  ), { dispatch: false });

  attachmentSent$ = createEffect(() =>
    this.action$.pipe(
      ofType(actions.attachmentSent),
      switchMap(({orderNumber, orderPhotos}) =>
        this.backCounterDashboardService.addAttachments(orderNumber, orderPhotos).pipe(
          map(() => BackCounterToolActions.getRequestAttachments({orderNumber})),
          catchError((error) => of(BackCounterToolActions.attachmentSentFailed({error})))
        )
      )
    )
  );

  getRequestNotes$ = createEffect(() =>
    this.action$.pipe(
      ofType(actions.getRequestNotes),
      switchMap(({orderNumber, pos, typeOf, requestType}) =>
        this.backCounterNotesService.getNotes(orderNumber, pos, typeOf, requestType).pipe(
          map((requestNotes) => actions.getRequestNotesSuccess({ requestNotes })),
          catchError((error) => of(actions.getRequestNotesFailed({ error })))
        )
      )
    )
  );

  priceOverride$ = createEffect(() =>
    this.action$.pipe(
      ofType(actions.priceOverride),
      switchMap(({part, branchCode, rewardNumber}) => {
        const modalRef = this.ngbModalService.open(PriceOverrideModalComponent, { size: 'lg' });
        modalRef.componentInstance.part = part;
        modalRef.componentInstance.openingFrom = 'BCT';
        modalRef.componentInstance.branchCode = branchCode;
        modalRef.componentInstance.rewardNumber = rewardNumber;
        return of({result: modalRef.result, part});
      }),
      switchMap(({result, part}) => {
        return result.then((res) => {
          this.store.dispatch(BackCounterToolActions.syncUnsavedChanges({
            orderNumber: part.orderNumber,
            editedParts: [{
              ...part,
              unitPrice: res.overridePrice,
              itemTotal: res.overridePrice,
              manualPrice: res.overridePrice
            }]
          }));
        });
      })
    ), { dispatch: false });

  getInventory$ = createEffect(() =>
    this.action$.pipe(
      ofType(actions.getInventory),
      filter(({items}) => items.length > 0),
      switchMap(({items, branchCode}) => {
        let getInventoryRequest: GetInventoryRequest = {
            branchCode,
            rewardNumber: '',
            partInventories: items.map(m => ({partNumber: m.materialNumber, partId: ''} as PartInventory))
          };
        this.loaderService.loading = true;
        return this.nationalInventoryService.getInventoryObs(getInventoryRequest).pipe(
          map((inventories: InventoryResult[]) => {
            return actions.getInventorySuccess({inventories});
          }),
          catchError(error => {
            this.toastService.errorMessage('STOModalComponent', 'getInventory', 'getInventory', error);
            return of(actions.getInventoryFailed({error}));
          }),
          tap(() => this.loaderService.loading = false)
        );
      })
    )
  );

  showStockTransferModal$ = createEffect(() =>
    this.action$.pipe(
      ofType(actions.showStockTransferModal),
      tap(
        ({requestOrderItems, requestBranchCode, orderNumber}) => {
          const modalRef = this.ngbModalService.open(StockTransferOrderComponent);
          modalRef.componentInstance.openingFrom = 'BCT';
          modalRef.componentInstance.requestOrderItems = requestOrderItems;
          modalRef.componentInstance.requestBranchCode = requestBranchCode;
          modalRef.componentInstance.orderNumber = orderNumber;
        }
      )
    ), { dispatch: false }
  );
  
  showKnownPartBuyoutModal$ = createEffect(() =>
    this.action$.pipe(
      ofType(actions.showKnownPartBuyoutModal),
      map(({requestOrderItems, orderNumber, requestBranchCode}) => {
        const modalRef = this.ngbModalService.open(KnownPartBuyoutComponent);
        modalRef.componentInstance.openingFrom = 'BCT';
        modalRef.componentInstance.requestOrderItems = requestOrderItems;
        modalRef.componentInstance.orderNumber = orderNumber;
          modalRef.componentInstance.requestBranchCode = requestBranchCode;
      })
    ), { dispatch: false }
  );
  
  showHotFlagModal$ = createEffect(() =>
    this.action$.pipe(
      ofType(actions.showHotFlagModal),
      tap(({ editHotFlagCode, requestOrderItems, orderNumber, requestBranchCode, esn }) => {
        const modalRef = this.ngbModalService.open(HotFlagComponent);
        modalRef.componentInstance.editHotFlagCode = editHotFlagCode;
        if(requestOrderItems) {
          modalRef.componentInstance.requestOrderItems = requestOrderItems;
        }
        modalRef.componentInstance.openingFrom = 'BCT';
        modalRef.componentInstance.orderNumber = orderNumber;
        modalRef.componentInstance.requestBranchCode = requestBranchCode;
        modalRef.componentInstance.esn = esn;
      })
    ), { dispatch: false }
  );

  getPartBinLocations$ = createEffect(() =>
    this.action$.pipe(
      ofType(actions.getPartBinLocations),
      switchMap(({ branchCode, partNumbers }) => {
        return this.partService.getPartBinLocations(partNumbers, branchCode).pipe(
          map((partBinLocationCollection) =>
            actions.getPartBinLocationsSuccess({ partBinLocationCollection })
          ),
          catchError(error => {
            return of(actions.getPartBinLocationsFailed({error}));
          }),
        )
      })
    )
  );

  readyToPickButtonPressed$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(actions.readyToPickButtonPressed),
        withLatestFrom(this.store.select(BackCounterToolSelectors.selectPartBinLocationCollection)),
        switchMap(([{sapOrderNo, items, orderNumber, pickRequestNumber, requestType}, selectedBins]) => {
          const partBins = this.mapBinLocations(items, selectedBins, sapOrderNo);
          return this.readyToPick(sapOrderNo, orderNumber, pickRequestNumber, partBins, requestType);
        })
      ),
    { dispatch: false }
  );
  
  mapBinLocations(orderItems: any[], overrideBins, orderNumber) {
    const _overrideBins: BinLocationDictionary = {};
      const _orderItems = orderItems?.map(x => {
        return {
          partNumber: x.partNumber,
          itemNum: x.itemNumber,
          orderNum: orderNumber,
          quantity: x.quantity
        };
      });
      for(let item of _orderItems) {
        const lookupBins: any[] = overrideBins?.[item.partNumber?.toLowerCase()];
        if (lookupBins?.length) {
          const selectedBin = lookupBins.find(x => x.fixedBin)?.binLocation ?? lookupBins[0]?.binLocation;
          const selectedBinType = lookupBins.find(x => x.fixedBin)?.storageType ?? lookupBins[0]?.storageType;
          _overrideBins[item.partNumber] = {...item, bins: lookupBins, selectedBin, selectedBinType};
        }
      }
      const selectedBins = Object.values(_overrideBins).map(x => {
        return {
          ItemNumber: x.itemNum,
          Quantity: x.quantity,
          StorageBin: x.selectedBin,
          StorageType: x.selectedBinType
        }
      });
      return selectedBins;
  }
  
  readyToPick(sapOrderNo: string, orderNumber: string, pickRequestNumber: string, partBins, requestType?: string) {
    this.loaderService.loading = true;
    return this.backCounterDashboardService
      .readyToPick({
        orderNumber: sapOrderNo,
        selectedBinLocations: partBins,
        backCounterTool: true,
        partRequestNumber: orderNumber,
        pickRequestNumber,
        requestType
      })
      .pipe(
        tap((response) => {
          if (response.pdf) {
            const filename = "TO_" + sapOrderNo;
            this.downloadService.downloadPDF(response.pdf, filename);
          }
          this.loaderService.loading = false;
          const successMessage = response.messages?.find(
            (f) =>
              f.type === "success" &&
              f.message.includes(
                "Event 'Ready to Pick' was executed successfully"
              )
          );
          const parsedMessages = response.messages.map((x) => {
            const toastTypeString =
              x.type.toString().charAt(0).toUpperCase() +
              x.type.toString().slice(1);
            const type: ToastType = ToastType[toastTypeString];
            return { ...x, type };
          });
          if (successMessage) {
            this.toastService.showToast(
              successMessage.message,
              ToastType.Success
            );
          } else {
            if( parsedMessages.length === 0 ){
              this.toastService.showToast("Something Went Wrong.", ToastType.Error);
            }else{
              this.toastService.showMultilineToast(parsedMessages);
            }
            this.store.dispatch(BackCounterToolActions.readyToPickFailed());
            return;
          }
          this.store.dispatch(BackCounterToolActions.readyToPickSuccess({ hasSuccessMessage: !!successMessage, orderNumber }));
        }),
        catchError((err) => {
          this.loaderService.loading = false;
          this.toastService.showToast("Something Went Wrong.", ToastType.Error);
          return err;
        })
      );
  }

  constructor(
    private readonly store: Store<AppState>,
    private readonly action$: Actions,
    private readonly backCounterDashboardService: BackCounterDashboardService,
    private readonly backCounterNotesService: BackCounterNotesService,
    private readonly toastService: ToastService,
    private readonly modalService: ModalService,
    private readonly vendorService: VendorService,
    private readonly router: Router,
    private readonly ngbModalService: NgbModal,
    private readonly downloadService: DownloadService,
    private readonly partService: PartService,
    private readonly customerService: CustomerService,
    private readonly loaderService: LoaderService,
    private readonly nationalInventoryService: NationalInventoryService,
    private readonly loggerService: LoggerService
  ) { }

  processPayload(existingItems: RequestOrderItem[], payload: {itemsToAdd: RequestOrderItem[], itemsToRemove: RequestOrderItem[], itemsToUpdate: RequestOrderItem[]}) {
    const newPayload = {
      itemsToAdd: [] as RequestOrderItem[],
      itemsToUpdate: [] as RequestOrderItem[],
      itemsToRemove: [] as RequestOrderItem[]
    };

    // consolidate itemsToAdd
    // payload.itemsToAdd = this.consolidateQuantities(payload.itemsToAdd);

    // If theres existing parts in the itemsToAdd array, convert to itemsToUpdate
    const itemsToUpdate = [] as RequestOrderItem[];
    payload.itemsToAdd = payload.itemsToAdd.filter(x => {
      const matchedPart = existingItems.find(y => y.materialNumber === x.materialNumber && y.unsavedChanges !== 'ADD' );
      if(matchedPart){
        const orderQuantity = Number(matchedPart.orderQuantity) + x.orderQuantity;
        itemsToUpdate.push({...matchedPart, orderQuantity, reqQuantity: orderQuantity});
      }
      return !matchedPart;
    });
    newPayload.itemsToUpdate = [...itemsToUpdate, ...payload.itemsToUpdate];
    newPayload.itemsToAdd = [...payload.itemsToAdd];
    newPayload.itemsToRemove = [...payload.itemsToRemove];
    return newPayload;
  }
  
  consolidateQuantities(items: RequestOrderItem[]): RequestOrderItem[] {
    const consolidatedMap = new Map<string, RequestOrderItem>();

    for (const item of items) {
      const key = item.materialNumber;

      if (consolidatedMap.has(key)) {
        const existingItem = consolidatedMap.get(key);
        existingItem.reqQuantity = (existingItem.reqQuantity || 0) + (item.reqQuantity || 0);
        existingItem.orderQuantity += item.orderQuantity;
      } else {
        consolidatedMap.set(key, { ...item });
      }
    }

    return Array.from(consolidatedMap.values());
  }

  public checkMaximumActiveRequests(orderNumber: string, openRequests: BackCounterRequestDetails[]): boolean {
    const allowed = openRequests.filter(x => x.orderNumber !== orderNumber).length < BackCounterConfig.activeRequestsLimit;
    if (!allowed) {
      this.toastService.showToast(ToastMessages.ERROR_LIMIT, ToastType.Error);
      this.router.navigate([BackCounterConfig.routes.dashboard]);
    }
    return allowed;
  }

  getGeneralRequestNotes(orderNumber: string): Observable<RequestNote[]> {
    return this.backCounterDashboardService.getGeneralRequestNotes(orderNumber).pipe(
      this.convertNoteTypes()
    );
  }

  private convertNoteTypes() {
    return map((bctnotes: BackCounterNote[]) => bctnotes.map<RequestNote>((x): RequestNote => {
      const theDate = new Date( Date.parse(x.utcTimestamp));
      return {
        userId: x.userid,
        techName: x.techname,
        techNumber: x.techno,
        noteText: x.notetext,
        date: x.utcTimestamp? theDate.toLocaleDateString() : x.wdate,
        time: x.utcTimestamp? theDate.toLocaleTimeString() : x.wtime
      };
  }));
  }

  updateHotFlagValue$ = createEffect(() =>
    this.action$.pipe(
      ofType(actions.updateHotFlagValue),
      withLatestFrom(this.store.select(BackCounterToolSelectors.selectBackCounterOpenRequests)),
      map(([action, openRequests]) => {
        const editedParts = openRequests.find(r => r.orderNumber === action.orderNumber)?.ordertogetitem;
        const parts = editedParts?.filter(f => f._id in action.items);
        const transformedParts = parts.map(m => 
          transformRequestOrderItemForHotFlag(
          m, 
          action.items[m._id].hotFlagValue, 
          action.items[m._id].vendor, 
          action.items[m._id].knownPartPrice));
        // You may need to transform editedParts or part as needed
        return actions.syncUnsavedChanges({
          orderNumber: action.orderNumber,
          editedParts: transformedParts,
          vin: action.vin,
          esn: action.esn,
          crc: action.crc
        });
      })
    )
  );

  updateStoValue$ = createEffect(() =>
    this.action$.pipe(
      ofType(actions.updateStoValue),
      withLatestFrom(this.store.select(BackCounterToolSelectors.selectBackCounterOpenRequests)),
      map(([action, openRequests]) => {
        const editedParts = openRequests.find(r => r.orderNumber === action.orderNumber)?.ordertogetitem;
        const thePart = editedParts?.find(p => p._id === action.id);

        // You may need to transform editedParts or part as needed
        return actions.syncUnsavedChanges({
          orderNumber: action.orderNumber,
          editedParts: [ transformRequestOrderItemForSTO(thePart, action.stockTransferOrder, action.knownPartPrice, action.quantity)]
        });
      })
    )
  );

  updateConfirmedPartsBuyOut$ = createEffect(() =>
    this.action$.pipe(
      ofType(actions.confirmPartsBuyout),
      withLatestFrom(this.store.select(BackCounterToolSelectors.selectBackCounterOpenRequests)),
      map(([action, openRequests]) => {
        const editedParts = openRequests.find(r => r.orderNumber === action.orderNumber)?.ordertogetitem;
        const buyoutItems = action.items ?? {};
        const thePartsList = (editedParts ?? []).filter(p => p._id in buyoutItems);

        // You may need to transform editedParts or part as needed
        return actions.syncUnsavedChanges({
          orderNumber: action.orderNumber,
          editedParts:  thePartsList.map( m => transformRequestOrderItemForBuyout(m, buyoutItems[m._id]))
        });
      })
    )
  );

  cancelRequest$ = createEffect(() =>
    this.action$.pipe(
      ofType(actions.cancelRequest),
      switchMap(({ orderNumber }) =>
        this.modalService
          .open(CancelBctRequestModalComponent, {}, { size: "sm" })
          .pipe(
            switchMap((isCancel) => {
              if (isCancel) {
                return this.backCounterDashboardService.cancelRequest(orderNumber).pipe(
                  map((cartId) => {
                    return actions.cancelRequestSuccess({ orderNumber });
                  }),
                  catchError((error) =>
                    of(actions.cancelRequestFailed({ error }))
                  )
                );
              } else {
                return EMPTY;
              }
            })
          )
      )
    )
  );
}

function findRequestOrderNumberByRequestType(requestDetails: BackCounterRequestDetails): string {
  // For pick requests, use pick request number.
  if (requestDetails.requestType === PICK ) {
    return requestDetails.pickreqno;
  }
  
  // For all other request types, use the standard orderNumber
  return requestDetails.orderNumber;
}

